import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheck,
  faCircleCheck,
  faCircleDot,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ActionCentreIcon from "../../assets/icons/Icon_Library/boom_action_centre_icons/icon_action_centre.svg2";
import BroadcastIcon from "../../assets/icons/create_broadcast.svg2";
import CampaignIcon from "../../assets/icons/create_inbound.svg2";
import ApiIcon from "../../assets/icons/create_api_connection.svg2";
import ContactsIcon from "../../assets/icons/contacts_main.svg2";
import Subheading from "../Common/Subheading";
import { pageProps } from "../helpers/pageProps";
import { useGetContactsQuery } from "../store/slices/contactsSlice";
import {
  useGetApiConnectionsQuery,
  useGetUsersSettingsDataQuery,
} from "../store/slices/settingsSlice";
import { useGetSentSmsBroadcastCompletionQuery } from "../store/slices/widgetsSlice";
import { useGetInboundCampaignsQuery } from "../store/inboundCampaignSlice";
import TrialDataModal from "./TrialDataModal";

const groupHoverDelay = 300;

const actionGroups = [
  {
    id: "broadcast",
    title: "Broadcast",
    description: "Send a message to your audience.",
    Icon: BroadcastIcon,
    actions: [
      {
        id: "create-broadcast-contact",
        title: "Add a Contact",
        description: "Add a contact before sending your broadcast.",
        detail:
          "Add a contact now so you have someone ready to include when you create your broadcast.",
        help:
          "A contact needs a mobile number before they can receive an SMS. You can add more details now or update the contact later.",
        to: "/assets/contacts/add",
        cta: "Create contact",
      },
      {
        id: "send-one-way-sms",
        title: "Send 1 way SMS",
        description: "Create and send a one-way SMS broadcast to your audience.",
        detail:
          "Build a one-way SMS broadcast, choose the people you want to reach, and decide when your message should be sent.",
        help:
          "One-way SMS broadcasts are for sending a message without collecting replies. Choose a sender, write your message, then select the contacts who should receive it.",
        to: "/library/broadcast/new/settings",
        cta: "Send 1 way SMS",
      },
      {
        id: "send-two-way-sms",
        title: "Send 2 way SMS",
        description: "Create and send a two-way SMS broadcast to your audience.",
        detail:
          "Build a two-way SMS broadcast, choose the people you want to reach, and decide when your message should be sent.",
        help:
          "Two-way SMS broadcasts let recipients reply to your message. Set up the message, audience, and the route where replies should be received before sending.",
        to: "/library/broadcast/new/settings",
        state: { commsMethod: "two-way" },
        cta: "Send 2 way SMS",
      },
    ],
  },
  {
    id: "campaign",
    title: "Campaign",
    description: "Collect and manage inbound responses.",
    Icon: CampaignIcon,
    actions: [
      {
        id: "create-campaign",
        title: "Create a campaign",
        description: "Set up a number or keyword to collect responses.",
        detail:
          "Create an inbound campaign so people can start a conversation with your organisation and receive the right response.",
        help:
          "Inbound campaigns use a number or keyword to receive messages. Configure the campaign details and response rules so incoming messages are handled correctly.",
        to: "/library/campaigns",
        cta: "Create campaign",
      },
      {
        id: "review-campaigns",
        title: "Review campaigns",
        description: "Manage your campaigns and incoming messages.",
        detail:
          "Review the campaigns you have created and manage the messages they receive in one place.",
        help:
          "Review campaign settings, keywords, and incoming messages from the campaigns list. You can update an existing campaign whenever your requirements change.",
        to: "/library/campaigns",
        cta: "View campaigns",
      },
    ],
  },
  {
    id: "api",
    title: "API",
    description: "Connect Boomerang with your own systems.",
    Icon: ApiIcon,
    actions: [
      {
        id: "create-api-connection",
        title: "Create an API connection",
        description: "Connect your applications to Boomerang.",
        detail:
          "Set up an API connection to send messages and use Boomerang features from the systems you already use.",
        help:
          "Choose the API connection type that matches your integration, then follow the setup steps to securely connect your application to Boomerang.",
        to: "/library/api-connections/add/api-type",
        cta: "Create API connection",
      },
      {
        id: "review-api-connections",
        title: "Review API connections",
        description: "View and manage your configured connections.",
        detail:
          "Review the API connections available to your account and continue configuring the integrations that support your workflow.",
        help:
          "Review the connection status and configuration of each integration from the API connections list. You can continue or update a connection at any time.",
        to: "/library/api-connections",
        cta: "View API connections",
      },
    ],
  },
  {
    id: "advanced-options",
    title: "Advanced options",
    description: "Configure people and account access.",
    Icon: ContactsIcon,
    actions: [
      {
        id: "create-contact",
        title: "Create a contact",
        description: "Add an individual contact for your messages.",
        detail:
          "Add a contact now so you have someone ready to include when you create your next broadcast.",
        help:
          "A contact needs a mobile number before they can receive an SMS. You can add more details now or update the contact later.",
        to: "/assets/contacts/add",
        cta: "Create contact",
      },
      {
        id: "invite-user",
        title: "Set up an additional user",
        description: "Invite a colleague to your account.",
        detail:
          "Give colleagues access to your Boomerang account so they can help manage messaging and account configuration.",
        help:
          "Invite a colleague using their email address, then choose the account access they need. They will receive an invitation to finish setting up their access.",
        to: "/settings/users",
        cta: "Manage users",
      },
    ],
  },
];

const SYSTEM_CONFIGURATION_STORAGE_KEY = "boomerang-action-centre-system-configuration";

const systemConfigurationGroup = {
  id: "system-configuration",
  title: "System configuration",
  description: "Complete these required setup tasks before using your purchased account.",
  Icon: ActionCentreIcon,
  priority: true,
  actions: [
    {
      id: "choose-primary-inbound-number",
      title: "Choose your primary inbound number",
      description: "Choose the number customers use to contact your organisation.",
      detail:
        "Your trial inbound number is not retained after purchase. Choose a primary inbound number for your live account before publishing it to customers.",
      help:
        "Number availability, geography, ranges, prices, and payment are still being defined. This task records that your primary inbound number selection is complete; it does not provision or purchase a number.",
      cta: "Mark number selected",
      completionMode: "detail-panel",
    },
    {
      id: "complete-billing-details",
      title: "Complete billing details",
      description: "Confirm the billing information for your account.",
      detail:
        "Check that your organisation name, address, payment terms, and tax details are ready for invoicing.",
      help:
        "Accurate billing details make sure invoices and purchase records are sent to the right organisation with the information your finance team needs. This prototype task does not update billing records.",
      cta: "Mark billing details complete",
      completionMode: "detail-panel",
    },
    {
      id: "add-billing-contact",
      title: "Add a billing contact",
      description: "Choose who receives billing communications and invoices.",
      detail:
        "Add the name and email address of the person responsible for billing communications.",
      help:
        "Your billing contact receives communications related to subscriptions and invoices. Use an address monitored by the person or team responsible for payments.",
      cta: "Save billing contact",
      completionMode: "detail-panel",
    },
  ],
};

const getActions = (groups) => groups.flatMap((group) =>
  group.actions.map((action) => ({ ...action, groupId: group.id, groupTitle: group.title }))
);

const getSavedSystemConfiguration = () => {
  try {
    return JSON.parse(window.localStorage.getItem(SYSTEM_CONFIGURATION_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

export default function ActionCentre() {
  const location = useLocation();
  const navigate = useNavigate();
  const savedState = location.state?.actionCentreState;
  const isPostPurchase = new URLSearchParams(location.search).get("post-purchase") === "1";
  const [completedSystemConfigurationActions, setCompletedSystemConfigurationActions] = useState(
    getSavedSystemConfiguration
  );
  const { data: contactsData } = useGetContactsQuery({
    page: 0,
    perPage: 1,
    search: "",
  });
  const { data: oneWayBroadcastsData } = useGetSentSmsBroadcastCompletionQuery({
    direction: "1-Way",
  });
  const { data: twoWayBroadcastsData } = useGetSentSmsBroadcastCompletionQuery({
    direction: "2-Way",
  });
  const { data: campaignsData } = useGetInboundCampaignsQuery({
    limit: 1,
    offset: 0,
    search: "",
  });
  const { data: apiConnectionsData } = useGetApiConnectionsQuery({
    status: "",
    kind: "",
    orderBy: "used-last",
    search: "",
    offset: 0,
    limit: 1,
  });
  const { data: usersData } = useGetUsersSettingsDataQuery({
    limit: 2,
    offset: 0,
    order: "asc",
  });
  const hasSentBroadcast = (data) =>
    (data?.total ?? 0) > 0 || (data?.reports?.length ?? 0) > 0;
  const completedActions = {
    "create-broadcast-contact": (contactsData?.pagination?.total ?? 0) > 0,
    "send-one-way-sms": hasSentBroadcast(oneWayBroadcastsData),
    "send-two-way-sms": hasSentBroadcast(twoWayBroadcastsData),
    "create-campaign": (campaignsData?.total ?? 0) > 0,
    "create-api-connection": (apiConnectionsData?.total ?? 0) > 0,
    "create-contact": (contactsData?.pagination?.total ?? 0) > 0,
    "invite-user": (usersData?.total ?? 0) > 1,
  };
  const displayedActionGroups = isPostPurchase ? [systemConfigurationGroup, ...actionGroups] : actionGroups;
  const primaryActionGroups = isPostPurchase ? actionGroups : displayedActionGroups;
  const actions = getActions(displayedActionGroups).map((action) => ({
    ...action,
    complete:
      completedActions[action.id] ||
      action.complete ||
      (action.groupId === systemConfigurationGroup.id && completedSystemConfigurationActions.includes(action.id)),
  }));
  const actionGroupProgress = actionGroups.map((group) => {
    const groupActions = actions.filter((action) => action.groupId === group.id);

    return {
      id: group.id,
      completed: groupActions.filter((action) => action.complete).length,
      total: groupActions.length,
    };
  });
  const getProgressLabel = ({ completed, total }) => {
    if (completed === 0) {
      return "No actions completed";
    }

    return `${completed} of ${total} action${total === 1 ? "" : "s"} completed`;
  };
  const [hoveredGroupId, setHoveredGroupId] = useState(null);
  const groupHoverTimeoutRef = useRef(null);
  const [selectedActionId, setSelectedActionId] = useState(
    savedState?.selectedActionId ?? null
  );
  const [hoveredHelpId, setHoveredHelpId] = useState(null);
  const [isNextActionDetailOpen, setIsNextActionDetailOpen] = useState(false);
  const [openSystemConfigurationActionId, setOpenSystemConfigurationActionId] = useState(null);
  const [billingContact, setBillingContact] = useState({
    name: "",
    email: "",
    phone: "",
    companyNumber: "",
  });

  useEffect(() => {
    window.localStorage.setItem(
      SYSTEM_CONFIGURATION_STORAGE_KEY,
      JSON.stringify(completedSystemConfigurationActions)
    );
  }, [completedSystemConfigurationActions]);

  useEffect(() => {
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {
        actionCentreState: {
          selectedActionId,
        },
      },
    });
  }, [location.pathname, location.search, navigate, selectedActionId]);

  const selectedAction = actions.find((action) => action.id === selectedActionId);
  const isDetailPanelFocused =
    selectedAction?.completionMode === "detail-panel" &&
    selectedAction.groupId !== systemConfigurationGroup.id;
  const nextSystemConfigurationAction = actions.find(
    (action) => action.groupId === systemConfigurationGroup.id && !action.complete
  );
  const selectedGroupActions =
    selectedAction?.groupId === systemConfigurationGroup.id || !selectedAction ? [] : [selectedAction];

  useEffect(
    () => () => {
      clearTimeout(groupHoverTimeoutRef.current);
    },
    []
  );

  const showGroupActions = (group) => {
    setHoveredGroupId(group.id);

    if (selectedAction?.groupId === group.id) {
      return;
    }

    const [firstAction] = group.actions;
    setSelectedActionId(firstAction.id);
    setHoveredHelpId(firstAction.id);
  };

  const handleGroupMouseEnter = (group) => {
    clearTimeout(groupHoverTimeoutRef.current);
    groupHoverTimeoutRef.current = setTimeout(() => showGroupActions(group), groupHoverDelay);
  };

  const handleGroupMouseLeave = () => {
    clearTimeout(groupHoverTimeoutRef.current);
  };

  const selectAction = (action) => {
    setSelectedActionId(action.id);
    setHoveredHelpId(action.id);
  };

  const showHelp = (actionId) => {
    setHoveredHelpId(actionId);
  };

  const hideHelp = (actionId) => {
    setHoveredHelpId((helpId) => (helpId === actionId ? null : helpId));
  };

  const toggleSystemConfigurationAction = (action) => {
    setCompletedSystemConfigurationActions((completedActions) =>
      completedActions.includes(action.id)
        ? completedActions.filter((actionId) => actionId !== action.id)
        : [...completedActions, action.id]
    );
  };

  const toggleSystemConfigurationDetails = (action) => {
    setOpenSystemConfigurationActionId((openActionId) =>
      openActionId === action.id ? null : action.id
    );
  };

  const completeNextSystemConfigurationAction = () => {
    if (nextSystemConfigurationAction) {
      toggleSystemConfigurationAction(nextSystemConfigurationAction);
      setIsNextActionDetailOpen(false);
    }
  };

  const openNextActionDetails = () => {
    if (nextSystemConfigurationAction) {
      setHoveredGroupId(systemConfigurationGroup.id);
      setOpenSystemConfigurationActionId(nextSystemConfigurationAction.id);
    }
  };

  const isSystemConfigurationActionComplete = (action) =>
    completedSystemConfigurationActions.includes(action.id);

  const renderActionGroup = (group, groupIndex) => {
    const isOpen = hoveredGroupId === group.id;

    return (
      <section
        key={group.id}
        className={`action-centre__group${group.priority ? " action-centre__group--priority" : ""}`}
        tabIndex="0"
        onMouseEnter={() => handleGroupMouseEnter(group)}
        onMouseLeave={handleGroupMouseLeave}
        onFocus={() => handleGroupMouseEnter(group)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            handleGroupMouseLeave();
          }
        }}
      >
        <div className="action-centre__group-intro">
          <div className="action-centre__group-heading">
            <span className="action-centre__group-number" aria-hidden="true">{groupIndex + 1}</span>
            <group.Icon className="action-centre__group-icon" width={36} aria-hidden="true" />
            <div className="action-centre__group-copy">
              <h3 className="h5 fw-bold mb-0">{group.title}</h3>
              <p className="small text-secondary mb-0">{group.description}</p>
            </div>
          </div>
        </div>

        <div id={`${group.id}-actions`} className={`action-centre__group-actions${isOpen ? " is-open" : ""}`}>
          <div className="action-centre__group-actions-content">
            {isOpen && group.actions.map((action) => {
              const isSelected = action.id === selectedActionId;
              const isComplete =
                completedActions[action.id] ||
                action.complete ||
                isSystemConfigurationActionComplete(action);
              const isSystemConfigurationAction = group.id === systemConfigurationGroup.id;
              const isDetailsOpen = openSystemConfigurationActionId === action.id;

              return (
                <React.Fragment key={action.id}>
                  <div className={`action-centre__action ${isSelected ? "is-selected" : ""}`}>
                    <button
                      type="button"
                      className="action-centre__action-toggle"
                      aria-label={`Select ${action.title}`}
                      onClick={() => selectAction(action)}
                    >
                      <span className={isComplete ? "welcome-to-trial__completed-action" : "action-centre__action-status"}>
                        <FontAwesomeIcon icon={isComplete ? faCheck : faCircleDot} aria-hidden="true" />
                      </span>
                      <span className="action-centre__action-copy text-start flex-grow-1">
                        <strong>{action.title}</strong>
                        <small>{action.description}</small>
                      </span>
                    </button>
                    {isSystemConfigurationAction ? (
                      <button
                        type="button"
                        className="action-centre__action-cta"
                        aria-expanded={isDetailsOpen}
                        aria-controls={`${action.id}-details`}
                        onClick={() => toggleSystemConfigurationDetails(action)}
                      >
                        {isDetailsOpen ? "Hide setup details" : "Open setup details"}
                      </button>
                    ) : (
                      <Link
                        to={action.to}
                        className="action-centre__action-cta"
                        state={{
                          ...action.state,
                          actionCentreState: { selectedActionId: action.id },
                        }}
                        onClick={() => selectAction(action)}
                      >
                        {action.cta}
                      </Link>
                    )}
                  </div>
                  {isSystemConfigurationAction && isDetailsOpen && (
                    <div
                      id={`${action.id}-details`}
                      className="action-centre__setup-details"
                      role="region"
                      aria-label={`${action.title} setup details`}
                    >
                      <p>{action.detail}</p>
                      <p className="text-secondary mb-3">{action.help}</p>
                      {action.id === "add-billing-contact" ? (
                        <form
                          className="action-centre__billing-contact-form"
                          onSubmit={(event) => {
                            event.preventDefault();
                            toggleSystemConfigurationAction(action);
                            setOpenSystemConfigurationActionId(null);
                          }}
                        >
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold" htmlFor="billing-contact-name">
                                Full name
                              </label>
                              <input
                                id="billing-contact-name"
                                className="form-control"
                                type="text"
                                name="billingContactName"
                                autoComplete="name"
                                value={billingContact.name}
                                onChange={(event) => setBillingContact((contact) => ({
                                  ...contact,
                                  name: event.target.value,
                                }))}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold" htmlFor="billing-contact-email">
                                Email address
                              </label>
                              <input
                                id="billing-contact-email"
                                className="form-control"
                                type="email"
                                name="billingContactEmail"
                                autoComplete="email"
                                value={billingContact.email}
                                onChange={(event) => setBillingContact((contact) => ({
                                  ...contact,
                                  email: event.target.value,
                                }))}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold" htmlFor="billing-contact-phone">
                                Phone number
                              </label>
                              <input
                                id="billing-contact-phone"
                                className="form-control"
                                type="tel"
                                name="billingContactPhone"
                                autoComplete="tel"
                                value={billingContact.phone}
                                onChange={(event) => setBillingContact((contact) => ({
                                  ...contact,
                                  phone: event.target.value,
                                }))}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold" htmlFor="billing-contact-company-number">
                                Company number
                              </label>
                              <input
                                id="billing-contact-company-number"
                                className="form-control"
                                type="text"
                                name="billingContactCompanyNumber"
                                autoComplete="off"
                                value={billingContact.companyNumber}
                                onChange={(event) => setBillingContact((contact) => ({
                                  ...contact,
                                  companyNumber: event.target.value,
                                }))}
                                required
                              />
                            </div>
                          </div>
                          <button
                            type="submit"
                            className="action-centre__setup-details-complete btn bg-colored-reverse text-white fw-bold"
                          >
                            {isComplete ? "Mark incomplete" : action.cta}
                          </button>
                        </form>
                      ) : (
                        <button
                          type="button"
                          className="action-centre__setup-details-complete btn bg-colored-reverse text-white fw-bold"
                          onClick={() => {
                            toggleSystemConfigurationAction(action);
                            setOpenSystemConfigurationActionId(null);
                          }}
                        >
                          {isComplete ? "Mark incomplete" : action.cta}
                        </button>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  return (
    <main className="action-centre pb-5">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Subheading
          Icon={ActionCentreIcon}
          heading="Action Centre"
          subheading="Choose an action to make the most of your trial."
        />
      </div>

      <section className="action-centre__summary bg-colored-reverse text-white justify-content-between text-start rounded-4 py-2 py-md-3 px-4 mb-4" aria-label="Trial summary">
        <div className="action-centre__summary-overview">
          <div className="action-centre__summary-overview-item">
            <span className="action-centre__summary-label">System Type:</span>
            <strong>{pageProps.isTrial ? "Trial" : "Purchased"}</strong>
          </div>
          <div className="action-centre__summary-overview-item action-centre__summary-overview-item--center">
            <span className="action-centre__summary-label">Status:</span>
            <strong>{pageProps.trialStatus === "expired" ? "Expired" : "Active"}</strong>
          </div>
          <div className="action-centre__summary-overview-item">
            <TrialDataModal
              buttonClassName="action-centre__trial-details-button px-4 py-2"
            />
          </div>
        </div>
        <div className="action-centre__summary-progress">
          <div className="action-centre__summary-progress-item action-centre__summary-progress-item--start">
            {[actionGroupProgress[0], actionGroupProgress[3]].map((progress) => {
              const group = actionGroups.find((actionGroup) => actionGroup.id === progress.id);

              return (
                <div key={progress.id} className="action-centre__summary-progress-row">
                  <span className="action-centre__summary-label">{group.title}:</span>
                  <strong>{getProgressLabel(progress)}</strong>
                </div>
              );
            })}
          </div>
          <div className="action-centre__summary-progress-item action-centre__summary-progress-item--center">
            {[actionGroupProgress[1]].map((progress) => {
              const group = actionGroups.find((actionGroup) => actionGroup.id === progress.id);

              return (
                <React.Fragment key={progress.id}>
                  <span className="action-centre__summary-label">{group.title}:</span>
                  <strong>{getProgressLabel(progress)}</strong>
                </React.Fragment>
              );
            })}
          </div>
          <div className="action-centre__summary-progress-item action-centre__summary-progress-item--end">
            {[actionGroupProgress[2]].map((progress) => {
              const group = actionGroups.find((actionGroup) => actionGroup.id === progress.id);

              return (
                <React.Fragment key={progress.id}>
                  <span className="action-centre__summary-label">{group.title}:</span>
                  <strong>{getProgressLabel(progress)}</strong>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      <div className={`action-centre__workspace row g-4${isDetailPanelFocused ? " action-centre__workspace--detail-focused" : ""}`}>
        <section className="action-centre__workspace-actions col-lg-7" aria-labelledby="actions-heading">
          {isPostPurchase && (
            <>
              <section className="action-centre__next-action action-centre__panel rounded-4 mb-4" aria-labelledby="next-action-heading">
                {nextSystemConfigurationAction ? (
                  <>
                    <p className="action-centre__next-action-eyebrow mb-1">Your next action</p>
                    <h2 id="next-action-heading" className="h4 fw-bold mb-1">{nextSystemConfigurationAction.title}</h2>
                    <p className="text-secondary mb-3">{nextSystemConfigurationAction.description}</p>
                    <div className="action-centre__next-action-controls">
                      {nextSystemConfigurationAction.completionMode === "detail-panel" ? (
                        <button type="button" className="btn bg-colored-reverse text-white fw-bold" onClick={openNextActionDetails}>
                          Open setup details
                        </button>
                      ) : (
                        <>
                          <button type="button" className="btn bg-colored-reverse text-white fw-bold" onClick={completeNextSystemConfigurationAction}>
                            Complete this step
                          </button>
                          <button
                            type="button"
                            className="action-centre__next-action-details"
                            aria-expanded={isNextActionDetailOpen}
                            aria-controls="next-action-detail"
                            onClick={() => setIsNextActionDetailOpen((isOpen) => !isOpen)}
                          >
                            {isNextActionDetailOpen ? "Hide details" : "What you need to do"}
                          </button>
                        </>
                      )}
                    </div>
                    {isNextActionDetailOpen && (
                      <div id="next-action-detail" className="action-centre__next-action-detail">
                        <p className="mb-0">{nextSystemConfigurationAction.detail}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="action-centre__next-action-eyebrow mb-1">Your required setup is complete</p>
                    <h2 id="next-action-heading" className="h4 fw-bold mb-1">Your account is ready to use</h2>
                    <p className="text-secondary mb-0">Explore the optional actions below to get more from your account.</p>
                  </>
                )}
              </section>

              <section className="action-centre__system-configuration action-centre__panel action-centre__panel--system rounded-4 mb-4" aria-label="System configuration">
                <p className="text-secondary mb-3 mt-2">Required setup for your purchased account.</p>
                <div className="action-centre__groups">
                  {renderActionGroup(systemConfigurationGroup, 0)}
                </div>
              </section>
            </>
          )}
          <div className="action-centre__panel action-centre__panel--optional rounded-4 p-3 p-md-4">
            <div className="mb-3">
              <h2 id="actions-heading" className="h4 fw-bold mb-1">Actions</h2>
              <p className="text-secondary mb-0">
                {isPostPurchase
                  ? "Explore the optional actions below."
                  : "Select an action to see the next step."}
              </p>
            </div>

            <div className="action-centre__groups">
              {primaryActionGroups.map(renderActionGroup)}
            </div>
          </div>
        </section>

        <aside className="action-centre__workspace-detail col-lg-5" aria-live="polite">
          <section className="action-centre__detail rounded-4 p-4 h-100" aria-labelledby="selected-action-heading">
            <h2 id="selected-action-heading" className="h3 fw-bold mb-0">
              Further details
            </h2>
            {isDetailPanelFocused ? (
              <div className="action-centre__task-detail mt-3">
                <h3 className="h5 fw-bold">{selectedAction.title}</h3>
                <p>{selectedAction.detail}</p>
                <p className="text-secondary small">{selectedAction.help}</p>
                <button
                  type="button"
                  className="btn bg-colored-reverse text-white fw-bold"
                  onClick={completeSelectedSystemConfigurationAction}
                >
                  {selectedAction.complete ? "Mark incomplete" : selectedAction.cta}
                </button>
              </div>
            ) : (
              <>
                <p className="text-secondary mb-0 mt-2">Click any action to learn about more features.</p>
                <div className="action-centre__detail-help-list mt-3">
                  {selectedGroupActions.map((action) => {
                const isHelpOpen = hoveredHelpId === action.id;
                const helpLabel = `Help with ${action.title} (${action.groupTitle})`;

                return (
                  <div
                    key={action.id}
                    className="action-centre__detail-help"
                    tabIndex="0"
                    onMouseEnter={() => showHelp(action.id)}
                    onMouseLeave={() => hideHelp(action.id)}
                    onFocus={() => showHelp(action.id)}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        hideHelp(action.id);
                      }
                    }}
                  >
                    <div className="action-centre__detail-help-label">
                      <span>
                        <FontAwesomeIcon icon={faQuestionCircle} aria-hidden="true" />
                        {helpLabel}
                      </span>
                    </div>
                    {isHelpOpen && (
                      <div id={`${action.id}-help`} className="action-centre__detail-help-content">
                        <p>{action.help}</p>
                        {action.groupId === systemConfigurationGroup.id ? (
                          <button
                            type="button"
                            className="action-centre__detail-help-link"
                            onClick={() => toggleSystemConfigurationAction(action)}
                          >
                            {action.complete ? "Mark incomplete" : action.cta}
                          </button>
                        ) : (
                          <Link
                            to={action.to}
                            className="action-centre__detail-help-link"
                            state={{
                              ...action.state,
                              actionCentreState: { selectedActionId: action.id },
                            }}
                          >
                            {action.cta} <FontAwesomeIcon icon={faArrowRight} className="ms-1" aria-hidden="true" />
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
                  })}
                </div>
              </>
            )}
          </section>
        </aside>
      </div>
    </main>
  );
}
